var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { GoogleAuthProvider, inMemoryPersistence, isSignInWithEmailLink, sendSignInLinkToEmail, setPersistence, signInWithEmailLink, signInWithPopup, signOut, } from "firebase/auth";
import { clientErrorFromResponse } from "../errors.js";
import { invariant } from "../utils.js";
import { RefreshToken, UserAuthState, getUserRefreshTokenPath, } from "./auth-state.js";
/**
 * Attampts to signin the user using Google client.
 */
export function signInWithGoogle(idauth) {
    return __awaiter(this, void 0, void 0, function* () {
        // using google to sign the user in
        const provider = new GoogleAuthProvider();
        yield setPersistence(idauth.firebaseAuth, inMemoryPersistence);
        try {
            const result = yield signInWithPopup(idauth.firebaseAuth, provider);
            const idtoken = yield result.user.getIdToken(false);
            return { type: "firebase", value: idtoken };
        }
        catch (err) {
            throw err;
        }
    });
}
export function createSessionWithToken(identity, token) {
    return __awaiter(this, void 0, void 0, function* () {
        invariant(token);
        invariant(token.type === "firebase", "only `firebase` is the supported token type");
        const res = yield fetch(new URL("/v1/auth/user/session/create", identity.baseUrl), {
            method: "POST",
            mode: "cors",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                clientType: token.type,
                idToken: token.value,
            }),
        });
        const body = yield res.json();
        if (!res.ok) {
            throw clientErrorFromResponse(res.status, body);
        }
        const data = body;
        const sess = UserAuthState.fromToken(data.session.token);
        identity.saveAuthState({
            session: sess,
            refreshTokenPath: getUserRefreshTokenPath(sess),
            refreshToken: data.refresh_token
                ? RefreshToken.fromRefreshJWT(data.refresh_token)
                : null,
        });
        return data;
    });
}
export function completeSignInFromEmailLink(idauth, locationHref) {
    return __awaiter(this, void 0, void 0, function* () {
        const fbauth = idauth.firebaseAuth;
        if (!isSignInWithEmailLink(fbauth, locationHref)) {
            throw new Error("unable to verify auth email session");
        }
        const sp = new URL(locationHref);
        const email = sp.searchParams.get("email");
        if (!email) {
            throw new Error("missing email information");
        }
        try {
            let result = yield signInWithEmailLink(fbauth, email, locationHref);
            const idtoken = yield result.user.getIdToken(false);
            return { type: "firebase", value: idtoken };
        }
        catch (err) {
            // trigger signout if signing in with email works
            yield signOut(fbauth);
            throw err;
        }
    });
}
// Random identier to tag keys stored on the browser
const RandomDeviceId = `device-${(Math.random() * 1000 + 1).toPrecision(6).toString()}`;
/**
 * Attempts to signin the user using and email, and sending over an
 * OTP link that they can use to join
 *
 * To complete the sign in. call `completeSignInFromEmailLink`
 */
export function attemptSignInWithEmailAddress(idauth, opts) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!opts) {
            throw new Error("require `opts.email` and `opts.callback` to be included");
        }
        if (!opts.email) {
            throw new Error("email input required");
        }
        // include simple verification key to tag request
        let verifyKey = `email-${new Date().getTime()}-${RandomDeviceId}`;
        const url = new URL(opts.callback);
        const sp = new URLSearchParams(url.searchParams);
        sp.set("email", opts.email);
        sp.set("email_auth_key", verifyKey);
        url.search = sp.toString();
        yield sendSignInLinkToEmail(idauth.firebaseAuth, opts.email, {
            url: url.toString(),
            handleCodeInApp: true,
        });
    });
}
export var Tenant;
(function (Tenant) {
    function createSession(identity, token, tenantId) {
        return __awaiter(this, void 0, void 0, function* () {
            invariant(token);
            invariant(token.type === "firebase", "only `firebase` token can be used");
            const res = yield fetch(new URL(`/v1/auth/tenant/${tenantId}/token`, identity.baseUrl), {
                method: "POST",
                mode: "cors",
                body: JSON.stringify({
                    clientType: token.type,
                    idToken: token.value,
                }),
                headers: {
                    "Content-Type": "application/json",
                },
            });
            const body = yield res.json();
            if (!res.ok) {
                throw clientErrorFromResponse(res.status, body);
            }
            const data = body;
            const sess = UserAuthState.fromToken(data.session.token);
            identity.saveAuthState({
                session: sess,
                refreshTokenPath: getUserRefreshTokenPath(sess),
                refreshToken: data.refresh_token
                    ? RefreshToken.fromRefreshJWT(data.refresh_token)
                    : null,
            });
            return data;
        });
    }
    Tenant.createSession = createSession;
    function refreshSession(identity, refreshToken, 
    /**
     * You can list a different domain to login with
     */
    domain) {
        return __awaiter(this, void 0, void 0, function* () {
            const res = yield fetch(new URL(`/v1/auth/tenant/${domain}/refresh`, identity.baseUrl), {
                method: "POST",
                mode: "cors",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    refreshToken: refreshToken,
                }),
            });
            const body = yield res.json();
            if (!res.ok) {
                throw clientErrorFromResponse(res.status, body);
            }
            const data = body;
            const sess = UserAuthState.fromToken(data.session.token);
            identity.saveAuthState({
                session: sess,
                refreshTokenPath: getUserRefreshTokenPath(sess),
                refreshToken: data.refresh_token
                    ? RefreshToken.fromRefreshJWT(data.refresh_token)
                    : null,
            });
        });
    }
    Tenant.refreshSession = refreshSession;
    function requestToken(identity, authtoken, domain) {
        return __awaiter(this, void 0, void 0, function* () {
            const res = yield fetch(new URL(`/v1/auth/tenant/${domain}/token`, identity.baseUrl), {
                method: "POST",
                mode: "cors",
                body: JSON.stringify({
                    clientType: "authclient",
                    authToken: authtoken,
                }),
                headers: {
                    "Content-Type": "application/json",
                },
            });
            const body = yield res.json();
            if (!res.ok) {
                throw clientErrorFromResponse(res.status, body);
            }
            const data = body;
            const sess = UserAuthState.fromToken(data.session.token);
            identity.saveAuthState({
                session: sess,
                refreshTokenPath: getUserRefreshTokenPath(sess),
                refreshToken: data.refresh_token
                    ? RefreshToken.fromRefreshJWT(data.refresh_token)
                    : null,
            });
            return data;
        });
    }
    Tenant.requestToken = requestToken;
})(Tenant || (Tenant = {}));
